/**
 * Mass-selection helpers — pure, because the pointer machinery around them is hard to
 * test and the RANGE MATHS is what must never be wrong (an off-by-one here silently
 * bulk-edits an item nobody chose).
 *
 * Two gestures produce ranges (maintainer request, 27 Jul — the app is used on phones
 * AND desktops, so both get a native-feeling path):
 *  - touch/pointer DRAG over the checkboxes (mobile): anchor row → row under finger,
 *    list auto-scrolling at the viewport edges while the drag is live;
 *  - SHIFT-CLICK (desktop): last-tapped row → shift-clicked row.
 *
 * Both apply their range over the selection as it stood when the gesture began: a
 * gesture that retreats shrinks its own range but never eats picks made before it.
 * The anchor's starting state picks the direction — see gestureSelection below.
 */

/** Inclusive id range between two row indexes, either direction. */
export function rangeIds(rows: readonly { id: string }[], fromIndex: number, toIndex: number): string[] {
  if (rows.length === 0) return [];
  const lo = Math.max(0, Math.min(fromIndex, toIndex));
  const hi = Math.min(rows.length - 1, Math.max(fromIndex, toIndex));
  if (lo > hi) return [];
  return rows.slice(lo, hi + 1).map((r) => r.id);
}

/**
 * The selection during a live gesture: what stood at gesture start, with the current
 * range applied. Rebuilt from the BASE each move, so retreating genuinely shrinks.
 *
 * THE GESTURE'S DIRECTION FOLLOWS THE ANCHOR'S STARTING STATE (field report, 28 Jul:
 * mass select worked both ways on mobile, but desktop had no mass UNselect). A gesture
 * that begins on an unselected row SELECTS the range; one that begins on a selected
 * row DESELECTS it — the same rule for drag and shift-click, so both devices get both
 * directions without a second gesture to learn.
 */
export function gestureSelection(
  base: ReadonlySet<string>,
  rows: readonly { id: string }[],
  anchorIndex: number,
  currentIndex: number,
): ReadonlySet<string> {
  const anchorId = rows[anchorIndex]?.id;
  const removing = anchorId !== undefined && base.has(anchorId);
  const next = new Set(base);
  for (const id of rangeIds(rows, anchorIndex, currentIndex)) {
    if (removing) next.delete(id);
    else next.add(id);
  }
  return next;
}

/** How far to auto-scroll for a pointer at `clientY`, in px per frame: 0 outside the
 * edge zones, ramping up the deeper into a zone the pointer sits. */
export function autoScrollStep(clientY: number, viewportHeight: number, zone = 72, maxStep = 24): number {
  if (clientY < zone) return -Math.ceil(((zone - clientY) / zone) * maxStep);
  const fromBottom = viewportHeight - clientY;
  if (fromBottom < zone) return Math.ceil(((zone - fromBottom) / zone) * maxStep);
  return 0;
}
